import React, { useState, useReducer, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Row,
  Col,
  Button,
  Table,
  Modal,
  Upload,
  List,
  Progress,
  Typography,
  message,
} from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import * as api from './api';

const UploadView = ({ disabled, onChange }) => {
  const [dialog, setDialog] = useState(false);
  const [progressList, dispatchProgressList] = useReducer((state, action) => {
    const data = { ...action.payload };
    switch (action.type) {
      case 'push':
        return state.concat(data);
      case 'update':
        var index = state.findIndex(item => item.id === data.id);
        state.splice(index, 1, data);
        return [...state];
      default:
        return action.payload;
    }
  }, []);
  useEffect(() => dispatchProgressList({ payload: [] }), [dialog]);

  const beforeUpload = file => {
    let msgText = null;
    if (
      ![
        'application/zip',
        'application/x-zip-compressed',
        'application/octet-stream',
      ].includes(file.type)
    )
      msgText = '文件类型不正确!';
    if (file.size >= 5 * 1024 * 1024 * 1024) msgText = '文件大小不能超过5GB!';
    if (msgText) {
      message.error(msgText);
      return Upload.LIST_IGNORE;
    } else return true;
  };

  const handleChange = useCallback(
    async data => {
      if (!onChange) return;
      const id = Date.now();
      const payload = {
        id,
        name: data.file.name,
        status: 'active',
        progress: 0,
      };
      dispatchProgressList({ type: 'push', payload });
      let res;
      try {
        res = onChange(data, progressEvent => {
          if (progressEvent.lengthComputable) {
            const num = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100
            );
            payload.progress = num;
            if (num >= 100) payload.status = 'success';
            dispatchProgressList({ type: 'update', payload });
          }
        });
        if (res instanceof Promise) res = await res;
      } catch (error) {
        payload.status = 'exception';
        dispatchProgressList({ type: 'update', payload });
      }
      return res;
    },
    [onChange]
  );
  return (
    <>
      <Button
        style={{ margin: '0 20px' }}
        type="primary"
        disabled={disabled}
        onClick={() => setDialog(true)}
      >
        新建
      </Button>
      <Modal
        visible={dialog}
        title="上传"
        footer={null}
        onCancel={() => setDialog(false)}
      >
        <Upload
          accept=".zip"
          listType="picture-card"
          beforeUpload={beforeUpload}
          customRequest={handleChange}
          showUploadList={false}
          disabled={disabled}
        >
          <div style={{ width: '100px' }}>
            <PlusOutlined />
          </div>
        </Upload>
        <Typography.Text style={{ marginTop: 8 }} type="secondary">
          仅支持zip文件格式且文件大小不能超过5GB
        </Typography.Text>
        {!!progressList.length && (
          <List
            dataSource={progressList}
            renderItem={item => (
              <List.Item>
                <List.Item.Meta
                  title={
                    <>
                      {item.progress < 100 ? <LoadingOutlined /> : null}
                      {item.name} {item.progress}
                    </>
                  }
                  description={
                    <Progress
                      strokeColor="#91B9CD"
                      trailColor="#FFF"
                      percent={item.progress}
                      status={item.status}
                    />
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    </>
  );
};

UploadView.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

const columns = [
  { title: '文件名', key: 'id', dataIndex: 'name' },
  { title: '上传时间', dataIndex: 'createAt' },
  { title: '状态', dataIndex: 'statusName' },
];

const UploadList = ({ id, disabled }) => {
  const { goBack } = useHistory();
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const handleQueryUploadList = useCallback(() => {
    setLoading(true);
    api
      .queryUploadList({ id })
      .then(res => setList(res.data || []))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(handleQueryUploadList, [handleQueryUploadList]);

  const handleUpload = useCallback(
    (data, onUploadProgress) =>
      api
        .handleUpload(
          { id, file: data.file, fileName: data.file.name },
          onUploadProgress
        )
        .then(handleQueryUploadList),
    [handleQueryUploadList, id]
  );

  return (
    <>
      <Row>
        <Col span={24}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={list}
            loading={loading}
            pagination={{ size: 'small' }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <Button onClick={goBack}>取消</Button>
          <UploadView
            style={{ margin: '0 20px' }}
            type="primary"
            htmlType="submit"
            onChange={handleUpload}
            disabled={disabled}
          >
            新建
          </UploadView>
        </Col>
      </Row>
    </>
  );
};

UploadList.propTypes = {
  id: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
};

export default UploadList;
